/**
 * iOS in standalone mode resolves `position: fixed; inset: 0` against a layout
 * viewport that is sometimes stale at launch, and never re-lays-out — so the
 * app box ends short of the screen and anything pinned to its bottom floats up
 * mid-page. Measuring the height ourselves and pinning it to a custom property
 * is the only reliable fix; `100dvh` remains the fallback for anything that
 * runs before this does.
 */
export function trackViewportHeight(): () => void {
  const apply = (): void => {
    // innerHeight, not visualViewport.height: the latter shrinks when the
    // keyboard opens, which would collapse the layout under the code entry
    // field rather than letting it scroll.
    document.documentElement.style.setProperty('--app-h', `${window.innerHeight}px`);
  };

  apply();
  // Re-measure whenever iOS might have changed its mind: rotation, returning
  // from the app switcher, and the launch resize that arrives after first paint.
  window.addEventListener('resize', apply);
  window.addEventListener('orientationchange', apply);
  window.addEventListener('pageshow', apply);
  document.addEventListener('visibilitychange', apply);
  const settle = window.setTimeout(apply, 300);

  return () => {
    window.removeEventListener('resize', apply);
    window.removeEventListener('orientationchange', apply);
    window.removeEventListener('pageshow', apply);
    document.removeEventListener('visibilitychange', apply);
    window.clearTimeout(settle);
  };
}
